/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {ReactElement} from 'react';
import {IconButtonProps as MUIIconButtonProps} from '@material-ui/core/IconButton';
import {UseOverrideStylesProps} from '../../hooks/useExtendStyles';
import {withDLSTheme} from '../../theme/withDLSTheme';
import {Color} from '../../types/types';
import {IconButton as CurrentIconButton} from './current/IconButton';
import {IconButton as LegacyIconButton} from './legacy/IconButton';

export enum IconButtonLabelPlacement {
  STACKED = 'stacked',
  HORIZONTAL = 'horizontal'
}

export interface IconButtonProps extends Omit<MUIIconButtonProps, 'color'>, UseOverrideStylesProps {
  elementId?: string;
  icon: ReactElement;
  isSelected?: boolean;
  label?: string;
  labelPlacement?: IconButtonLabelPlacement;
  hideLabel?: boolean;
  labelClass?: string | null;
  disableHover?: boolean;
  isDisableBackgroundColor?: boolean;
  backgroundColor?: null | string;
  color?: string | Color;
  shouldWrap?: boolean;
  padding?: string | null;
  disableHoverColor?: string;
  /**
   * Enables the compact outlined icon control style used by Back/Forward/Close buttons.
   */
  isControlButton?: boolean;
  /**
   * Outer button size for control mode.
   */
  controlButtonSize?: number;
  /**
   * Outer button padding for control mode.
   */
  controlButtonPadding?: number;
  /**
   * Inner icon frame size for control mode.
   */
  controlFrameSize?: number;
  /**
   * Icon size for control mode.
   */
  controlIconSize?: number;
  /**
   * Default border width for the control frame.
   */
  controlDefaultBorderWidth?: number;
  /**
   * Optional default filter applied to the inner control frame.
   */
  controlDefaultFilter?: string;
  /**
   * Optional hover filter applied on the inner control frame.
   */
  controlHoverFilter?: string;
  /**
   * Optional active shadow applied on the outer button in control mode.
   */
  controlActiveOuterShadow?: string;
  /**
   * Optional active filter applied on the outer button in control mode.
   */
  controlActiveFilter?: string;
  /**
   * Optional active shadow for the inner control frame.
   */
  controlActiveInnerShadow?: string;
}

/**
 * Theme-aware icon button.
 *
 * Supports both the existing icon button behavior and a compact
 * control-button variant used by `BackButton`, `ForwardButton`, and `CloseButton`.
 *
 * ```typescript
 * import React from 'react';
 * import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
 * import {MaterialBackIcon} from '@ion/dls/src/icons/MaterialBackIcon';
 *
 * const Example = () => (
 *   <>
 *     <IconButton icon={<MaterialBackIcon />} onClick={onDefaultIconClick} />
 *     <IconButton isControlButton icon={<MaterialBackIcon />} onClick={onControlClick} />
 *   </>
 * );
 * ```
 */
export const IconButton = withDLSTheme(LegacyIconButton, CurrentIconButton);

export default IconButton;
